import { createSlice, createSelector } from "@reduxjs/toolkit";

import { APPLICATION_STATUS } from "features/constants/application";
import { REPORT_STATUS } from "features/constants/report";

// Default permission objects (reuse the same object reference)
const DEFAULT_PERMISSIONS = {
  canEditFields: true,
  canUploadFiles: true,
  canDeleteFiles: true,
  canWriteComments: true,
};

const initialState = {
  applicationPermissions: {}, // { applicationId: { canEditFields, canUploadFiles, canDeleteFiles, canWriteComments } }
  reportPermissions: {}, // { reportId: { canEditFields, canUploadFiles, canDeleteFiles, canWriteComments } }
};

// Helper function to calculate permissions based on status
const calculateApplicationPermissions = (status) => {
  const isSubmittedOrValidated =
    status === APPLICATION_STATUS.SUBMITTED ||
    status === APPLICATION_STATUS.VALIDATED;

  return {
    canEditFields: !isSubmittedOrValidated,
    canUploadFiles: true, // Always allow file uploads
    canDeleteFiles: !isSubmittedOrValidated,
    canWriteComments: true, // Always allow comments
  };
};

const calculateReportPermissions = (status) => {
  // Similar logic for reports
  const isSubmittedOrValidated =
    status === REPORT_STATUS.SUBMITTED || status === REPORT_STATUS.VALIDATED;

  return {
    canEditFields: !isSubmittedOrValidated,
    canUploadFiles: true,
    canDeleteFiles: !isSubmittedOrValidated,
    canWriteComments: true,
  };
};
const statusPermissionsSlice = createSlice({
  name: "statusPermissions",
  initialState,
  reducers: {
    setApplicationStatusPermissions: (state, action) => {
      const { applicationId, status } = action.payload;
      if (applicationId && status) {
        state.applicationPermissions[applicationId] =
          calculateApplicationPermissions(status);
      }
    },
    setReportStatusPermissions: (state, action) => {
      const { reportId, status } = action.payload;
      if (reportId && status) {
        state.reportPermissions[reportId] = calculateReportPermissions(status);
      }
    },
    clearApplicationStatusPermissions: (state, action) => {
      const { applicationId } = action.payload;
      delete state.applicationPermissions[applicationId];
    },
    clearReportStatusPermissions: (state, action) => {
      const { reportId } = action.payload;
      delete state.reportPermissions[reportId];
    },
    clearAllStatusPermissions: (state) => {
      state.applicationPermissions = {};
      state.reportPermissions = {};
    },
  },
});

export const {
  setApplicationStatusPermissions,
  setReportStatusPermissions,
  clearApplicationStatusPermissions,
  clearReportStatusPermissions,
  clearAllStatusPermissions,
} = statusPermissionsSlice.actions;

export default statusPermissionsSlice.reducer;

// Memoized selectors
export const selectApplicationStatusPermissions = createSelector(
  [
    (state) => state.statusPermissions.applicationPermissions,
    (state, applicationId) => applicationId,
  ],
  (applicationPermissions, applicationId) => {
    return applicationPermissions[applicationId] || DEFAULT_PERMISSIONS;
  }
);

export const selectReportStatusPermissions = createSelector(
  [
    (state) => state.statusPermissions.reportPermissions,
    (state, reportId) => reportId,
  ],
  (reportPermissions, reportId) => {
    return reportPermissions[reportId] || DEFAULT_PERMISSIONS;
  }
);

// Helper functions to calculate permissions directly (for use in components that don't have access to ID)
// Memoize these to prevent unnecessary recalculations
const memoizedApplicationPermissions = new Map();
const memoizedReportPermissions = new Map();

export const getApplicationPermissionsByStatus = (status) => {
  if (!memoizedApplicationPermissions.has(status)) {
    memoizedApplicationPermissions.set(
      status,
      calculateApplicationPermissions(status)
    );
  }
  return memoizedApplicationPermissions.get(status);
};

export const getReportPermissionsByStatus = (status) => {
  if (!memoizedReportPermissions.has(status)) {
    memoizedReportPermissions.set(status, calculateReportPermissions(status));
  }
  return memoizedReportPermissions.get(status);
};
